import { c as _c } from "react/compiler-runtime";
import React from "react";
import { cn } from "@repo/ui/utils.ts";
import { PortableText, type PortableTextBlock, type PortableTextReactComponents } from "next-sanity";
import { asPortableText, hardBreakHandler, linkMarkHandler, onMissingPortableTextComponent, proseBaseClasses } from "./shared";
import { parseChildrenToSlug } from "@/lib/utils";
import type { RichTextTableBlock } from "@/lib/sanity/sanity.types";

/**
 * Dedicated component for the `richTextTable` schema.
 *
 * Schema permits everything `richTextRight` allows (`normal`, `h3`,
 * `blockquote`, lists, `strong`, `em`, `link`) plus the `richTextTableBlock`
 * embed. Renders tables as semantic HTML tables.
 */
export function DefaultTableBlock(t0) {
  const $ = _c(9);
  const {
    value
  } = t0;
  if (!value.rows || value.rows.length === 0) {
    return null;
  }
  const isFirstRowHeader = value.headerStyle === "firstRow" || value.headerStyle === "both";
  const isFirstColHeader = value.headerStyle === "firstColumn" || value.headerStyle === "both";
  let t1;
  if ($[0] !== isFirstColHeader || $[1] !== isFirstRowHeader || $[2] !== value.rows) {
    let t2;
    if ($[4] !== isFirstColHeader || $[5] !== isFirstRowHeader) {
      t2 = (row, rowIndex) => {
        const isHeaderRow = rowIndex === 0 && isFirstRowHeader;
        return <tr key={row._key}>{row.cells?.map((cell, cellIndex) => {
            const isHeaderCell = isHeaderRow || cellIndex === 0 && isFirstColHeader;
            const CellTag = isHeaderCell ? "th" : "td";
            return <CellTag key={cell._key} className={cn("border border-border-default p-4 text-left typography-body-xs-regular", isHeaderCell ? "typography-body-xs-medium text-text-primary" : "text-text-secondary")}><PortableText value={cell.content as unknown as PortableTextBlock[]} /></CellTag>;
          })}</tr>;
      };
      $[4] = isFirstColHeader;
      $[5] = isFirstRowHeader;
      $[6] = t2;
    } else {
      t2 = $[6];
    }
    t1 = value.rows.map(t2);
    $[0] = isFirstColHeader;
    $[1] = isFirstRowHeader;
    $[2] = value.rows;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[7] !== t1) {
    t2 = <div data-table={true} className="not-prose overflow-hidden rounded-md"><table className="w-full table-fixed border-collapse"><tbody>{t1}</tbody></table></div>;
    $[7] = t1;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  return t2;
}
const components: Partial<PortableTextReactComponents> = {
  block: {
    h3: ({
      children,
      value
    }) => {
      const slug = parseChildrenToSlug(value.children);
      return <h3 id={slug} className="typography-component-sm-medium desktop:typography-component-md-medium uppercase text-text-primary">
          {children}
        </h3>;
    },
    blockquote: ({
      children
    }) => <blockquote data-blockquote className="border-l border-border-default pl-3 typography-body-xs-regular desktop:typography-body-sm-regular text-text-secondary [&>p]:leading-[1.4] [&>p]:m-0">
        {children}
      </blockquote>
  },
  marks: {
    link: linkMarkHandler
  },
  types: {
    richTextTableBlock: ({
      value
    }: {
      value: RichTextTableBlock;
    }) => <DefaultTableBlock value={value} />
  },
  hardBreak: hardBreakHandler
};
interface RichTextTableProps<T = unknown> {
  richText?: T | null;
  className?: string;
}
export function RichTextTable(t0) {
  const $ = _c(9);
  const {
    richText,
    className
  } = t0;
  if (!richText) {
    return null;
  }
  let t1;
  if ($[0] !== className) {
    t1 = cn(proseBaseClasses, "typography-body-sm-regular desktop:typography-body-md-regular text-text-secondary", "[&>*+*]:mt-4 [&>*+h3]:mt-8 [&>*+[data-blockquote]]:mt-3 [&>[data-blockquote]+[data-blockquote]]:mt-0 [&>*+[data-table]]:mt-12", className);
    $[0] = className;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== richText) {
    t2 = asPortableText(richText);
    $[2] = richText;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t2) {
    t3 = <PortableText value={t2} components={components} onMissingComponent={onMissingPortableTextComponent} />;
    $[4] = t2;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== t1 || $[7] !== t3) {
    t4 = <div className={t1}>{t3}</div>;
    $[6] = t1;
    $[7] = t3;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  return t4;
}